<script setup lang="ts">
import type { CmsElementBioCircle } from "~/composables/useCustom";
import { getProductsById } from "~/server/customAPI";
import { Carousel, Slide, Navigation } from "vue3-carousel";
import { buildUrlPrefix, getProductRoute } from "@shopware/helpers";
import { useUrlResolver } from "#imports";
import SharedHeadline from "~/components/shared/SharedHeadline.vue";
import SharedTopline from "~/components/shared/SharedTopline.vue";

const { getUrlPrefix } = useUrlResolver();

const props = defineProps<{
  content: CmsElementBioCircle;
}>();

const { headlineText, toplineText, toplineType, headlineType, Boxes } =
  props.content?.data;

let ProductIds: any = [];

Boxes.forEach((product: any) => {
  ProductIds.push(product.boxProduct);
});

const searchResults = ref([]);
searchResults.value = await getProductsById({
  ids: ProductIds,
});

const settings = {
  itemsToShow: 4,
  itemsToScroll: 4,
  wrapAround: false,
  snapAlign: "start",
  autoplay: 0,
  transition: 600,
  pauseAutoplayOnHover: true,
  breakpoints: {
    // 200px and up
    200: {
      itemsToShow: 1,
      itemsToScroll: 1,
      snapAlign: "center",
    },

    500: {
      itemsToShow: 2,
      itemsToScroll: 2,
      snapAlign: "start",
    },
    800: {
      itemsToShow: 3,
      itemsToScroll: 3,
      snapAlign: "start",
    },
    1000: {
      itemsToShow: 4,
      itemsToScroll: 4,
      snapAlign: "start",
    },
  },
};

const productSlider = ref(null);
const currentSlide = ref(0);

const slideTo = (val: any) => {
  currentSlide.value = val;
};

const itemsToShow = computed(() => {
  return productSlider?.value?.data?.config?.itemsToShow;
});

const showNavigation = computed(() => {
  return itemsToShow.value < searchResults.value.elements.length;
});
</script>
<template>
  <section>
    <div class="mb-10">
      <div class="container px-0 xl:px-5">
        <SharedTopline
          :headline="toplineText"
          :headlineType="toplineType"
        ></SharedTopline>
        <SharedHeadline
          :headline="headlineText"
          :headlineType="headlineType"
        ></SharedHeadline>
      </div>
    </div>
    <div v-if="searchResults && searchResults.elements.length > 0">
      <div class="container max-w-[90rem] px-0 xl:px-10">
        <div id="ProductsCarousel" class="-mx-2.5">
          <Carousel
            v-bind="settings"
            ref="productSlider"
            v-model="currentSlide"
          >
            <Slide
              v-for="product of searchResults.elements"
              :key="product.id"
              class="bg-theme-green"
            >
              <div class="group slide text-center px-2.5 my-4">
                <NuxtLink
                  :to="buildUrlPrefix(getProductRoute(product), getUrlPrefix())"
                >
                  <figure
                    class="w-[12.75rem] h-[12.75rem] mx-auto mb-5 flex items-center justify-center relative aspect-square"
                  >
                    <NuxtImg
                      v-if="product.cover"
                      loading="lazy"
                      format="webp"
                      width="250"
                      class="h-full object-contain w-full h-full"
                      :src="product.cover.media.url"
                      alt="product.translated.name"
                    />
                  </figure>
                  <div class="px-10">
                    <p class="text-sm !mb-4">
                      {{ $t("product.productNumber") }}:
                      {{ product.productNumber }}
                    </p>
                    <h4 class="text-base font-bold mb-4">
                      {{ product.translated.name }}
                    </h4>
                  </div>
                </NuxtLink>

                <div class="mt-5">
                  <NuxtLink
                    class="btn-outline !py-[0.5rem]"
                    :to="
                      buildUrlPrefix(getProductRoute(product), getUrlPrefix())
                    "
                  >
                    {{ $t("learnMore") }}
                  </NuxtLink>
                </div>
              </div>
            </Slide>
            <template v-if="showNavigation" #addons>
              <div
                class="carousel__navigation-button pointer-events-none z-0 absolute bottom-0 md:bottom-auto top-1/4 md:top-2/4 max-w-[90rem] flex justify-between desktop:-left-[60px] desktop:-right-[60px] -left-5 -right-5 mx-auto px-3 laptop:px-10 [&>button]:static"
              >
                <Navigation>
                  <template #next>
                    <img
                      src="assets/icons/slide-right.svg"
                      alt="Next"
                      class="w-6 h-6"
                    />
                  </template>
                  <template #prev>
                    <img
                      src="assets/icons/slide-left.svg"
                      alt="Previous"
                      class="w-6 h-6"
                    />
                  </template>
                </Navigation>
              </div>
            </template>
          </Carousel>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
.bg-theme-green:hover {
  background: #e7f6ed;
}
.carousel__navigation-button :deep(.carousel__prev),
.carousel__navigation-button :deep(.carousel__next) {
  @apply bg-skin-icon text-skin-white w-12 h-12 rounded-full flex items-center justify-center;
  position: relative;
  pointer-events: all;
}
</style>
